import React, { useMemo, useState } from 'react';
import { format, formatDistanceToNow, isToday, isTomorrow } from 'date-fns';
import { Building2, Calendar, Clock, Radio, Users, Video, Edit2, Check, X, ListTodo, Plus, UserCog } from 'lucide-react';

import { ProjectMeeting, OrganizationMeeting, Project, User } from '../types';
import { parseDateLike } from '../utils/dateUtils';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import {
  getUpcomingMeetings,
  formatMeetingTime,
  isMeetingNow,
  isUserAttending
} from '../utils/meetingManagement';
import { getStatusColors } from '../constants/colorScheme';

type AnyMeeting = ProjectMeeting | OrganizationMeeting;

interface MeetingManagerProps {
  projectMeetings: ProjectMeeting[];
  orgMeetings: OrganizationMeeting[];
  currentUser: User;
  users: User[];
  projects: Project[];
  projectUsers?: User[];
  onCreateProjectMeeting?: (meeting: ProjectMeeting) => void;
  onCreateOrgMeeting?: (meeting: OrganizationMeeting) => void;
  onUpdateMeeting?: (meeting: ProjectMeeting | OrganizationMeeting) => void;
  onToggleActionItem?: (meetingId: string, itemId: string) => void;
  onAddActionItem?: (meetingId: string, text: string, assigneeId?: string) => void;
  onCreateTaskFromActionItem?: (meetingId: string, itemId: string) => Promise<void> | void;
}

const LIVE_MEETING_CARD_CLASS = getStatusColors('Completed').container;
const LIVE_MEETING_BADGE_CLASS = `border ${getStatusColors('Completed').badge}`;
const ATTENDING_BADGE_CLASS = `border ${getStatusColors('Active').badge}`;

export const MeetingManager: React.FC<MeetingManagerProps> = ({
  projectMeetings,
  orgMeetings,
  currentUser,
  users,
  projects,
  projectUsers,
  onUpdateMeeting,
  onToggleActionItem,
  onAddActionItem,
  onCreateTaskFromActionItem,
}) => {
  const [activeTab, setActiveTab] = useState<'upcoming' | 'project' | 'org'>('upcoming');
  const [editingMeetingId, setEditingMeetingId] = useState<string | null>(null);
  const [editingNotes, setEditingNotes] = useState<string>('');
  const [newActionTexts, setNewActionTexts] = useState<Record<string, string>>({});
  const [newActionAssignees, setNewActionAssignees] = useState<Record<string, string>>({});
  const [editingAttendeesId, setEditingAttendeesId] = useState<string | null>(null);
  const [editingAttendeeIds, setEditingAttendeeIds] = useState<string[]>([]);

  const attendeePool = projectUsers || users;

  const sortMeetings = (meetings: AnyMeeting[]): AnyMeeting[] => {
    return [...meetings].sort((left, right) => {
      const leftDate = parseDateLike(left.scheduledAt);
      const rightDate = parseDateLike(right.scheduledAt);
      return (leftDate?.getTime() || 0) - (rightDate?.getTime() || 0);
    });
  };

  const sortedProjectMeetings = useMemo(
    () => sortMeetings(projectMeetings) as ProjectMeeting[],
    [projectMeetings]
  );

  const sortedOrgMeetings = useMemo(
    () => sortMeetings(orgMeetings) as OrganizationMeeting[],
    [orgMeetings]
  );

  const allMeetings = useMemo(
    () => sortMeetings([...projectMeetings, ...orgMeetings]),
    [orgMeetings, projectMeetings]
  );

  const upcomingMeetings = useMemo(() => {
    return getUpcomingMeetings(allMeetings);
  }, [allMeetings]);

  const myMeetings = useMemo(() => {
    return upcomingMeetings.filter((meeting) => isUserAttending(meeting, currentUser.id));
  }, [upcomingMeetings, currentUser.id]);

  const liveMeetings = useMemo(
    () => allMeetings.filter((meeting) => isMeetingNow(meeting.scheduledAt, meeting.duration)),
    [allMeetings]
  );

  const todayMeetings = useMemo(
    () =>
      upcomingMeetings.filter((meeting) => {
        const scheduledDate = parseDateLike(meeting.scheduledAt);
        return scheduledDate ? isToday(scheduledDate) : false;
      }),
    [upcomingMeetings]
  );

  const nextMeeting = upcomingMeetings[0] || null;

  const getUserName = (userId: string): string => {
    return users.find(u => u.id === userId)?.name || 'Unknown';
  };

  const getProjectName = (projectId: string): string => {
    return projects.find(p => p.id === projectId)?.name || 'Unknown Project';
  };

  const isProjectMeeting = (meeting: AnyMeeting): meeting is ProjectMeeting => {
    return 'projectId' in meeting;
  };

  const formatMeetingDate = (scheduledAt: string): string => {
    const date = parseDateLike(scheduledAt);
    if (!date) {
      return 'Date TBD';
    }

    if (isToday(date)) {
      return `Today, ${format(date, 'MMM d')}`;
    }

    if (isTomorrow(date)) {
      return `Tomorrow, ${format(date, 'MMM d')}`;
    }

    return format(date, 'EEE, MMM d');
  };

  const formatMeetingDistance = (scheduledAt: string): string => {
    const date = parseDateLike(scheduledAt);
    if (!date) {
      return 'Time unavailable';
    }

    return formatDistanceToNow(date, { addSuffix: true });
  };

  const getMeetingScopeLabel = (meeting: AnyMeeting): string => {
    if (isProjectMeeting(meeting)) {
      return 'Project';
    }

    return `${meeting.meetingType.charAt(0).toUpperCase()}${meeting.meetingType.slice(1)}`;
  };

  const renderEmptyState = (title: string, description: string) => (
    <div className="rounded-2xl border border-dashed border-border bg-muted/40 px-5 py-10 text-center">
      <p className="text-sm font-medium text-foreground">{title}</p>
      <p className="mt-2 text-sm text-muted-foreground">{description}</p>
    </div>
  );

  const handleEditNotes = (meeting: AnyMeeting) => {
    setEditingMeetingId(meeting.id);
    setEditingNotes(meeting.notes || '');
  };

  const handleSaveNotes = (meeting: AnyMeeting) => {
    if (onUpdateMeeting) {
      const updatedMeeting = { ...meeting, notes: editingNotes };
      onUpdateMeeting(updatedMeeting);
    }
    setEditingMeetingId(null);
    setEditingNotes('');
  };

  const handleCancelEdit = () => {
    setEditingMeetingId(null);
    setEditingNotes('');
  };

  const handleEditAttendees = (meeting: AnyMeeting) => {
    setEditingAttendeesId(meeting.id);
    setEditingAttendeeIds([...meeting.attendeeIds]);
  };

  const handleSaveAttendees = (meeting: AnyMeeting) => {
    if (onUpdateMeeting) {
      onUpdateMeeting({ ...meeting, attendeeIds: editingAttendeeIds });
    }
    setEditingAttendeesId(null);
  };

  const handleAddActionItemLocal = (meetingId: string) => {
    const text = (newActionTexts[meetingId] || '').trim();
    if (!text) return;
    if (onAddActionItem) {
      const assigneeId = newActionAssignees[meetingId] || undefined;
      onAddActionItem(meetingId, text, assigneeId);
    }
    setNewActionTexts((prev) => ({ ...prev, [meetingId]: '' }));
    setNewActionAssignees((prev) => ({ ...prev, [meetingId]: '' }));
  };

  const MeetingCard: React.FC<{ meeting: AnyMeeting }> = ({ meeting }) => {
    const isNow = isMeetingNow(meeting.scheduledAt, meeting.duration);
    const attendeeCount = meeting.attendeeIds.length;
    const isMine = isUserAttending(meeting, currentUser.id);
    const relatedTaskCount = isProjectMeeting(meeting) ? meeting.relatedTaskIds?.length || 0 : 0;

    return (
      <Card className={`border-border bg-card shadow-none ${isNow ? LIVE_MEETING_CARD_CLASS : ''}`}>
        <CardContent className="px-5 py-5">
          <div className="space-y-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  {isNow && <Badge className={LIVE_MEETING_BADGE_CLASS}>Live now</Badge>}
                  <Badge variant="outline" className="border-border bg-card text-foreground">
                    {getMeetingScopeLabel(meeting)}
                  </Badge>
                  {isMine && (
                    <Badge variant="outline" className={ATTENDING_BADGE_CLASS}>
                      You&apos;re attending
                    </Badge>
                  )}
                </div>

                <h3 className="mt-3 text-base font-semibold text-foreground">{meeting.title}</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  {meeting.description || 'No agenda has been attached to this meeting yet.'}
                </p>
              </div>

              {meeting.recordingUrl && (
                <a
                  href={meeting.recordingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-sm font-medium text-foreground hover:bg-accent"
                >
                  <Video className="h-4 w-4" />
                  Recording
                </a>
              )}
            </div>

            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-xl bg-muted/40 px-3 py-3">
                <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  <Calendar className="h-3.5 w-3.5" />
                  Date
                </div>
                <div className="mt-2 text-sm font-semibold text-foreground">{formatMeetingDate(meeting.scheduledAt)}</div>
                <div className="mt-1 text-xs text-muted-foreground">{formatMeetingDistance(meeting.scheduledAt)}</div>
              </div>

              <div className="rounded-xl bg-muted/40 px-3 py-3">
                <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  <Clock className="h-3.5 w-3.5" />
                  Time
                </div>
                <div className="mt-2 text-sm font-semibold text-foreground">
                  {formatMeetingTime(meeting.scheduledAt, meeting.duration)}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">{meeting.duration} min session</div>
              </div>

              <div className="rounded-xl bg-muted/40 px-3 py-3">
                <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  <Users className="h-3.5 w-3.5" />
                  Attendees
                </div>
                <div className="mt-2 text-sm font-semibold text-foreground">{attendeeCount} invited</div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {meeting.attendeeIds.slice(0, 3).map(getUserName).join(', ') || 'No attendees listed'}
                </div>
              </div>

              <div className="rounded-xl bg-muted/40 px-3 py-3">
                <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {isProjectMeeting(meeting) ? <Building2 className="h-3.5 w-3.5" /> : <Radio className="h-3.5 w-3.5" />}
                  Focus
                </div>
                <div className="mt-2 text-sm font-semibold text-foreground">
                  {isProjectMeeting(meeting) ? getProjectName(meeting.projectId) : getMeetingScopeLabel(meeting)}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {isProjectMeeting(meeting)
                    ? `${relatedTaskCount} linked task${relatedTaskCount === 1 ? '' : 's'}`
                    : 'Organization-wide coordination'}
                </div>
              </div>
            </div>

            {(meeting.notes || attendeeCount > 0 || isMine) && (
              <div className="flex flex-col gap-3 border-t border-border pt-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0 flex-1">
                  {editingMeetingId === meeting.id ? (
                    <div className="space-y-3">
                      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Edit Notes</div>
                      <textarea
                        value={editingNotes}
                        onChange={(e) => setEditingNotes(e.target.value)}
                        placeholder="Add meeting notes here..."
                        className="w-full min-h-[100px] p-3 rounded-md border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleSaveNotes(meeting)}
                          className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                        >
                          <Check className="h-4 w-4" />
                          Save
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm font-medium text-foreground hover:bg-muted"
                        >
                          <X className="h-4 w-4" />
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-center justify-between">
                        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Notes</div>
                        {(isMine || isProjectMeeting(meeting)) && (
                          <button
                            onClick={() => handleEditNotes(meeting)}
                            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
                            title="Edit notes"
                          >
                            <Edit2 className="h-3.5 w-3.5" />
                            Edit
                          </button>
                        )}
                      </div>
                      <p className="mt-2 text-sm text-muted-foreground">
                        {meeting.notes ? `${meeting.notes.slice(0, 160)}${meeting.notes.length > 160 ? '...' : ''}` : 'Notes will appear here once the meeting has been documented.'}
                      </p>
                    </div>
                  )}
                </div>
                <div className="lg:max-w-xs">
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Attendees</div>
                    {isProjectMeeting(meeting) && (
                      <button
                        onClick={() => editingAttendeesId === meeting.id ? handleSaveAttendees(meeting) : handleEditAttendees(meeting)}
                        className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
                        title={editingAttendeesId === meeting.id ? 'Save attendees' : 'Edit attendees'}
                      >
                        {editingAttendeesId === meeting.id ? <Check className="h-3.5 w-3.5" /> : <UserCog className="h-3.5 w-3.5" />}
                        {editingAttendeesId === meeting.id ? 'Save' : 'Edit'}
                      </button>
                    )}
                  </div>
                  {editingAttendeesId === meeting.id ? (
                    <div className="mt-2 space-y-1">
                      {attendeePool.map((u) => (
                        <label key={u.id} className="flex items-center gap-2 text-xs text-foreground cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editingAttendeeIds.includes(u.id)}
                            onChange={(e) =>
                              setEditingAttendeeIds((prev) =>
                                e.target.checked ? [...prev, u.id] : prev.filter((id) => id !== u.id)
                              )
                            }
                            className="rounded"
                          />
                          {u.name || u.email}
                        </label>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {meeting.attendeeIds.slice(0, 4).map((attendeeId) => (
                        <span
                          key={attendeeId}
                          className="rounded-full border border-border bg-card px-2.5 py-1 text-xs text-muted-foreground"
                        >
                          {getUserName(attendeeId)}
                        </span>
                      ))}
                      {attendeeCount > 4 && (
                        <span className="rounded-full border border-border bg-card px-2.5 py-1 text-xs text-muted-foreground">
                          +{attendeeCount - 4} more
                        </span>
                      )}
                      {attendeeCount === 0 && (
                        <span className="text-xs text-muted-foreground italic">No attendees listed</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Action Items – only for project meetings */}
            {isProjectMeeting(meeting) && (
              <div className="border-t border-border pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <ListTodo className="h-4 w-4 text-muted-foreground" />
                  <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Action Items</span>
                </div>
                <div className="space-y-2 mb-3">
                  {(meeting.actionItems || []).length === 0 && (
                    <p className="text-xs text-muted-foreground italic">No action items yet.</p>
                  )}
                  {(meeting.actionItems || []).map((item) => (
                    <div key={item.id} className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        checked={item.done}
                        onChange={() => onToggleActionItem && onToggleActionItem(meeting.id, item.id)}
                        className="mt-0.5 rounded"
                        title="Toggle action item complete"
                      />
                      <div className="flex-1 min-w-0">
                        <span className={`text-sm ${item.done ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                          {item.text}
                        </span>
                        {item.assigneeId && (
                          <span className="ml-2 text-xs text-muted-foreground">
                            → {getUserName(item.assigneeId)}
                          </span>
                        )}
                        {item.linkedTaskId && (
                          <a
                            href={`/task/${item.linkedTaskId}`}
                            className="ml-2 text-xs text-primary hover:underline"
                          >
                            View Task
                          </a>
                        )}
                        {!item.linkedTaskId && onCreateTaskFromActionItem && (
                          <button
                            onClick={() => onCreateTaskFromActionItem(meeting.id, item.id)}
                            className="ml-2 text-xs text-primary hover:underline"
                            title="Create task from this action item"
                          >
                            Create Task
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {onAddActionItem && (
                  <div className="flex gap-2 items-start">
                    <input
                      type="text"
                      placeholder="New action item..."
                      value={newActionTexts[meeting.id] || ''}
                      onChange={(e) =>
                        setNewActionTexts((prev) => ({ ...prev, [meeting.id]: e.target.value }))
                      }
                      onKeyDown={(e) => e.key === 'Enter' && handleAddActionItemLocal(meeting.id)}
                      className="flex-1 rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      title="Action item text"
                    />
                    {attendeePool.length > 0 && (
                      <select
                        title="Assign to"
                        value={newActionAssignees[meeting.id] || ''}
                        onChange={(e) =>
                          setNewActionAssignees((prev) => ({ ...prev, [meeting.id]: e.target.value }))
                        }
                        className="rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      >
                        <option value="">Assign…</option>
                        {attendeePool.map((u) => (
                          <option key={u.id} value={u.id}>{u.name || u.email}</option>
                        ))}
                      </select>
                    )}
                    <button
                      onClick={() => handleAddActionItemLocal(meeting.id)}
                      className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90"
                      title="Add action item"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Add
                    </button>
                  </div>
                )}
              </div>
            )}
            
          </div>
        </CardContent>
      </Card>
    );
  };

  const visibleMeetings =
    activeTab === 'upcoming'
      ? upcomingMeetings
      : activeTab === 'project'
        ? sortedProjectMeetings
        : sortedOrgMeetings;

  return (
    <Card className="border-border bg-card shadow-sm">
      <CardHeader className="pb-4">
        <div className="space-y-1">
          <CardTitle className="text-xl text-foreground">Meetings</CardTitle>
          <p className="text-sm text-muted-foreground">
            Surface what is live now, what is next, and which sessions need your attention.
          </p>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-2xl border border-border bg-card px-4 py-4">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Live now</div>
            <div className="mt-2 text-2xl font-semibold text-foreground">{liveMeetings.length}</div>
            <p className="mt-2 text-sm text-muted-foreground">
              {liveMeetings[0]?.title || 'No meeting is in progress right now.'}
            </p>
          </div>

          <div className="rounded-2xl border border-border bg-card px-4 py-4">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Next up</div>
            <div className="mt-2 text-base font-semibold text-foreground">
              {nextMeeting?.title || 'No upcoming meeting'}
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              {nextMeeting
                ? `${formatMeetingDate(nextMeeting.scheduledAt)} · ${formatMeetingTime(nextMeeting.scheduledAt, nextMeeting.duration)}`
                : 'Your next meeting will appear here once it is scheduled.'}
            </p>
          </div>

          <div className="rounded-2xl border border-border bg-card px-4 py-4">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Today</div>
            <div className="mt-2 text-2xl font-semibold text-foreground">{todayMeetings.length}</div>
            <p className="mt-2 text-sm text-muted-foreground">
              {todayMeetings.length > 0
                ? `${todayMeetings[0].title} is on today's agenda.`
                : 'No meetings are scheduled for today.'}
            </p>
          </div>

          <div className="rounded-2xl border border-border bg-card px-4 py-4">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">You&apos;re attending</div>
            <div className="mt-2 text-2xl font-semibold text-foreground">{myMeetings.length}</div>
            <p className="mt-2 text-sm text-muted-foreground">
              {upcomingMeetings.length > 0
                ? `${myMeetings.length} of ${upcomingMeetings.length} upcoming meeting${upcomingMeetings.length === 1 ? '' : 's'} include you.`
                : 'No upcoming meetings are scheduled yet.'}
            </p>
          </div>
        </div>

        {allMeetings.length === 0 && renderEmptyState(
          'No meetings scheduled yet',
          'Project and organization meetings will appear here once they are added to the data source.'
        )}

        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'upcoming' | 'project' | 'org')} className="space-y-4">
          <TabsList className="grid w-full grid-cols-3 lg:w-[520px]">
            <TabsTrigger value="upcoming">
              Upcoming ({upcomingMeetings.length})
            </TabsTrigger>
            <TabsTrigger value="project">
              Project ({sortedProjectMeetings.length})
            </TabsTrigger>
            <TabsTrigger value="org">
              Organization ({sortedOrgMeetings.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="upcoming" className="space-y-4">
            {visibleMeetings.length > 0 ? (
              <div className="space-y-4">
                {visibleMeetings.map((meeting) => (
                  <MeetingCard key={meeting.id} meeting={meeting} />
                ))}
              </div>
            ) : (
              renderEmptyState(
                'No upcoming meetings',
                'Nothing is scheduled in the next 30 days for this project or organization.'
              )
            )}
          </TabsContent>

          <TabsContent value="project" className="space-y-4">
            {visibleMeetings.length > 0 ? (
              <div className="space-y-4">
                {visibleMeetings.map((meeting) => (
                  <MeetingCard key={meeting.id} meeting={meeting} />
                ))}
              </div>
            ) : (
              renderEmptyState(
                'No project meetings',
                'Project-specific planning, standups, or execution reviews will appear here.'
              )
            )}
          </TabsContent>

          <TabsContent value="org" className="space-y-4">
            {visibleMeetings.length > 0 ? (
              <div className="space-y-4">
                {visibleMeetings.map((meeting) => (
                  <MeetingCard key={meeting.id} meeting={meeting} />
                ))}
              </div>
            ) : (
              renderEmptyState(
                'No organization meetings',
                'Administrative and department meetings will appear here once they are scheduled.'
              )
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default MeetingManager;
